// AddExpense.js (Fixed version)
import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom/cjs/react-router-dom.min";
import Navbar from "components/Navbars/AuthNavbar";
import Footer from "components/Footers/Footer";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import BASE_URL from "components/BaseUrl/apiConfig";
import Swal from "sweetalert2";
import BlockUiOverlay from "./BlockUiOverlay";
import { MoveLeft, Plus, Wallet, SquarePen } from "lucide-react";
const AddExpense = () => {
  const history = useHistory();
  const { id } = useParams(); // id = edit expense id
  const [loading, setLoading] = useState(false);
  const [currency, setCurrency] = useState("");
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [isEdit, setIsEdit] = useState(false);
  const [originalUserId, setOriginalUserId] = useState("");
  const [userData, setUserData] = useState({
    userId: "",
    groupId: "",
    userTypeId: "",
  });
  const [form, setForm] = useState({
    type: "",
    description: "",
    amount: "",
    transType: "1",
    date: new Date(),
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  // ===============================
  // LOAD USER DATA
  // ===============================
  useEffect(() => {
    const saved = localStorage.getItem("userData");
    if (saved) setUserData(JSON.parse(saved));
  }, []);
  // ===============================
  // FETCH CURRENCY + TYPES + EDIT DATA
  // ===============================
  useEffect(() => {
    if (!userData.userId || !userData.groupId) return;
    fetchCurrency();
    fetchExpenseTypes();
    // If editing
    if (id) {
      setIsEdit(true);
      fetchSingleExpense(id);
    }
  }, [userData, id]);
  // Fetch Currency
  const fetchCurrency = useCallback(async () => {
    try {
      const res = await fetch(
        `${BASE_URL}/api/Users/GetCurrencyByUserId?groupId=${userData.groupId}`
      );
      const data = await res.json();
      setCurrency(data?.currency || "USD");
    } catch {
      setCurrency("USD");
    }
  }, [userData]);
  // Fetch All Expense Types
  const fetchExpenseTypes = useCallback(async () => {
    try {
      const url = `${BASE_URL}/api/Expense/GetExpensebyUserId?orderId=${userData.groupId}&userId=${userData.userId}`;
      const res = await fetch(url);
      const data = await res.json();
      const sorted = (data?.expenseDescritions || []).sort((a, b) =>
        a.expenseTypeName.localeCompare(b.expenseTypeName)
      );
      setExpenseTypes(sorted);
    } catch {
      Swal.fire("Failed", "Could not load expense types", "warning");
    }
  }, [userData]);
  // Fetch Single Expense (Edit)
  const fetchSingleExpense = useCallback(
    async (expenseId) => {
      try {
        setLoading(true);
        const url = `${BASE_URL}/api/Expense/GetExpensebyUserIdandExpenseId?expenseId=${expenseId}&userId=${userData.userId}&userTypeId=${userData.userTypeId}`;
        const res = await fetch(url);
        if (!res.ok) throw new Error(await res.text());
        const data = await res.json();
        // Map expenseTypeId to "1" or "2" for the select (handles both number/string and "Cr./Dr." formats)
        const mappedTransType = (data.expenseTypeId === "Cr." || data.expenseTypeId === 1 || data.expenseTypeId === "1") ? "1" : "2";
        setForm({
          type: data.expenseDescType || "",
          description: data.description || "",
          amount: data.expenses || "",
          transType: mappedTransType,
          date: new Date(data.date),
        });
        setOriginalUserId(data.userId || userData.userId);
      } catch (err) {
        if (err.message.includes("You can not update this transaction")) {
          Swal.fire({
            icon: "error",
            title: "Sorry!",
            text: "You cannot update this transaction.",
          });
        } else {
          Swal.fire("Error", "Failed to load expense", "error");
        }
        history.push("/expenses");
      } finally {
        setLoading(false);
      }
    },
    [userData, history]
  );
  // ===============================
  // VALIDATION
  // ===============================
  const validate = () => {
    let e = {};
    if (!form.type) e.type = "Select an expense type";
    if (!form.amount || Number(form.amount) <= 0) e.amount = "Enter a valid amount";
    if (!form.date) e.date = "Select a valid date";
    setErrors(e);
    return Object.keys(e).length === 0;
  };
  // ===============================
  // HANDLERS
  // ===============================
  const updateField = (name, value) => {
    setForm((p) => ({ ...p, [name]: value }));
    setTouched((p) => ({ ...p, [name]: true }));
  };
  const handleAmount = (val) => {
    const clean = val.replace(/[^0-9.]/g, "");
    const parts = clean.split(".");
    updateField(
      "amount",
      parts.length === 2 ? parts[0] + "." + parts[1].slice(0, 2) : parts[0]
    );
  };
  const handleTypeChange = (value) => {
    updateField("type", value);
    if (value === "__addNewExpense__") {
      history.push("/addexpensetype");
      return;
    }
    const selected = expenseTypes.find((x) => x.expenseTypeName === value);
    if (selected) {
      updateField("description", selected.defaulttext || "");
      // Map to transType based on selected type's expenseTypeId (assuming it's 1 or 2)
      updateField("transType", selected.expenseTypeId?.toString() || "1");
    }
  };
  // ===============================
  // SUBMIT (ADD + EDIT)
  // ===============================
const handleSubmit = async (e) => {
  e.preventDefault();
  if (!validate()) return;
  const payload = {
    expenseId: isEdit ? Number(id) : 0,
    userId: isEdit ? originalUserId : userData.userId,
    expenseDescType: form.type,
    description: form.description,
    expenses: form.amount.toString(),
    expenseTypeId: form.transType,
    Date: form.date.toISOString(),
  };
  try {
    setLoading(true);
    const res = await fetch(`${BASE_URL}/api/Expense/AddExpense`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!res.ok) {
      // Read error details if available
      const errorText = await res.text();
      throw new Error(errorText || `HTTP error! Status: ${res.status}`);
    }
    // If response is OK, assume success (matches old implementation)
    // Optionally parse JSON for additional checks, but not required
    const resData = await res.json();
    Swal.fire(
      "Success!",
      isEdit ? "Expense updated successfully!" : "Expense added successfully!",
      "success"
    );
    history.push("/expenses");
  } catch (err) {
    console.log("API ERROR:", err);
    // Handle specific API errors like in old code
    if (err.message.includes("You can not update this transaction")) {
      Swal.fire({
        icon: "error",
        title: "Sorry!",
        text: "You cannot update this transaction.",
      });
    } else {
      Swal.fire("Error", "Failed to process expense. Please try again later.", "error");
    }
  } finally {
    setLoading(false);
  }
};
  // ===============================
  // UI
  // ===============================
  return (
    <>
      <Navbar />
      <BlockUiOverlay visible={loading} />
      <div className="min-h-screen px-4 py-6">
        <div className="max-w-2xl mx-auto">
          {/* HEADER */}
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <Wallet className="h-10 w-10" />
              <div>
                <h1 className="text-xl font-bold text-gray-800">
                  {isEdit ? "Edit Expense" : "Add New Expense"}
                </h1>
                <p className="text-gray-600">Record your transaction</p>
              </div>
            </div>
            <button
              onClick={() => history.push("/expenses")}
              className="new-back-btn flex text-lg items-center gap-3 px-3 py-2 bg-blue-50 hover:bg-gray-100 rounded-lg"
            >
              <MoveLeft /> Back
            </button>
          </div>
          {/* CARD */}
         <div
  className="rounded-lg mt-6 p-8 bg-white"
  style={{
    boxShadow:
      "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px"
  }}
>
            <form onSubmit={handleSubmit}>
              {/* EXPENSE TYPE */}
              <label className="block font-medium mb-2">
                Expense Type <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full px-3 py-2 border rounded-lg mb-3"
                value={form.type}
                required
                onChange={(e) => handleTypeChange(e.target.value)}
              >
                <option value="">Select type</option>
                {expenseTypes.map((t) => (
                  <option key={t.id} value={t.expenseTypeName}>
                    {t.expenseTypeName}
                  </option>
                ))}
                {userData.userTypeId !== "3" && (
                  <option value="__addNewExpense__" className="bg-blue-500 text-white">
                    + Add New Expense Type
                  </option>
                )}
              </select>
              {touched.type && errors.type && (
                <p className="text-red-500 text-sm mb-4">{errors.type}</p>
              )}
              {/* DESCRIPTION */}
              <label className="block font-medium mb-2">Description</label>
              <textarea
                className="w-full px-3 py-2 border rounded-lg mb-1"
                value={form.description}
                required
                onChange={(e) => updateField("description", e.target.value)}
                maxLength={150}
                rows={3}
              />
              <p className="text-xs text-gray-500 mb-3">{form.description.length}/150</p>
              {/* AMOUNT + TRANSACTION TYPE */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-3">
                {/* Amount */}
                <div className=" mr-4 ">
                  <label className="block font-medium mb-2">
                    Amount ({currency}) <span className="text-red-500">*</span>
                  </label>
                  <input
                    className="w-full px-3 py-2 border rounded-lg"
                    value={form.amount}
                    required
                    onChange={(e) => handleAmount(e.target.value)}
                  />
                  {touched.amount && errors.amount && (
                    <p className="text-red-500 text-sm">{errors.amount}</p>
                  )}
                </div>
                {/* Transaction Type */}
                <div>
                  <label className="block font-medium mb-2">Transaction Type</label>
                  <select
                    className="w-full px-3 py-2 border rounded-lg"
                    value={form.transType}
                    onChange={(e) => updateField("transType", e.target.value)}
                  >
                    <option value="1">Credited</option>
                    <option value="2">Debited</option>
                  </select>
                </div>
              </div>
              {/* DATE */}
              <label className="block font-medium mb-2">
                Date <span className="text-red-500">*</span>
              </label>
              <DatePicker
                selected={form.date}
                onChange={(d) => updateField("date", d)}
                maxDate={new Date()}
                className="w-full px-3 py-2 border rounded-lg mb-4"
                dateFormat="MMM dd, yyyy"
              />
              {touched.date && errors.date && (
                <p className="text-red-500 text-sm mb-4">{errors.date}</p>
              )}
              {/* BUTTONS */}
              <div className="flex justify-end gap-4 mt-2">
                <button
                  type="button"
                  onClick={() => history.push("/expenses")}
                  className="px-6 py-2 border rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 flex items-center gap-2 bg-blue-500 text-white rounded-md shadow hover:bg-blue-600"
                  disabled={loading}
                >
                  {isEdit ? <SquarePen className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
                  {isEdit ? "Update Expense" : "Add Expense"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};
export default AddExpense;